import {
  useAnimals,
  useClient,
  useCreateSalesLot,
  useLots,
  useSalesLotAction,
  useSalesLots,
} from '@/api/hooks';
import { CreateSaleHerdLotModal } from '@/components/sales/CreateSaleHerdLotModal';
import { SalesSubnav } from '@/components/sales/SalesSubnav';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { getErrorMessage } from '@/lib/api';
import type { SalesLotStatus, SalesPricingUnit, SalesSaleType } from '@/types/api';
import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

const statusTone: Record<SalesLotStatus, 'default' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'default',
  open: 'info',
  full: 'success',
  slaughtered: 'warning',
  delivered: 'success',
};

const statusLabel: Record<SalesLotStatus, string> = {
  draft: 'Rascunho',
  open: 'Aberto',
  full: 'Completo',
  slaughtered: 'Abatido',
  delivered: 'Entregue',
};

const emptySaleForm = {
  sale_type: 'carcass_share' as SalesSaleType,
  pricing_unit: 'per_lb_hanging' as SalesPricingUnit,
  price_per_unit: '',
  source: 'animal' as 'animal' | 'lot',
  animal_id: '',
  source_lot_id: '',
  title: '',
  total_weight_kg: '',
  slaughter_date: '',
  pickup_location: '',
  slaughterhouse_type: '' as '' | 'custom_exempt' | 'inspected',
  establishment_number: '',
  notes: '',
};

function defaultPricingUnit(saleType: SalesSaleType): SalesPricingUnit {
  if (saleType === 'live_animal') return 'per_head';
  if (saleType === 'retail_cuts') return 'fixed_package';
  return 'per_lb_hanging';
}

export function SalesLotsPage() {
  const { data: client, isLoading: clientLoading } = useClient();
  const { propertyId } = usePropertyScope();
  const { data: sales = [], isLoading } = useSalesLots();
  const { data: animalsData } = useAnimals({
    property_id: propertyId ?? undefined,
    page: 1,
    per_page: 100,
  });
  const { data: lotsData, refetch: refetchLots } = useLots(propertyId);
  const createSale = useCreateSalesLot();
  const lotAction = useSalesLotAction();

  const [saleOpen, setSaleOpen] = useState(false);
  const [herdLotOpen, setHerdLotOpen] = useState(false);
  const [form, setForm] = useState(emptySaleForm);
  const [createError, setCreateError] = useState<string | null>(null);

  const animals = animalsData?.animals ?? [];
  const herdLots = lotsData?.lots ?? [];
  const selectedAnimal = useMemo(
    () => animals.find((a) => String(a.id) === form.animal_id),
    [animals, form.animal_id],
  );
  const selectedHerdLot = useMemo(
    () => herdLots.find((l) => String(l.id) === form.source_lot_id),
    [herdLots, form.source_lot_id],
  );

  const pricingOptions = useMemo(() => {
    if (form.sale_type === 'live_animal') {
      return [
        { value: 'per_head', label: 'Por cabeça' },
        { value: 'per_lb_live', label: '$/lb peso vivo' },
      ];
    }
    if (form.sale_type === 'retail_cuts') {
      return [
        { value: 'fixed_package', label: 'Preço fechado por pacote' },
        { value: 'per_lb_retail', label: '$/lb peso de corte' },
      ];
    }
    return [{ value: 'per_lb_hanging', label: '$/lb hanging weight' }];
  }, [form.sale_type]);

  if (!clientLoading && !client?.modules?.animal_sales) {
    return <Navigate to="/" replace />;
  }

  async function handleCreateSale(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    try {
      const body: Record<string, unknown> = {
        sale_type: form.sale_type,
        pricing_unit: form.pricing_unit,
        price_per_unit: form.price_per_unit ? Number(form.price_per_unit) : null,
        title: form.title,
        total_weight_kg: form.total_weight_kg ? Number(form.total_weight_kg) : null,
        slaughter_date: form.slaughter_date || null,
        pickup_location: form.pickup_location || null,
        slaughterhouse_type: form.slaughterhouse_type || null,
        establishment_number: form.establishment_number || null,
        notes: form.notes || null,
      };
      if (form.source === 'animal') {
        body.animal_id = Number(form.animal_id);
      } else {
        body.source_lot_id = Number(form.source_lot_id);
      }
      await createSale.mutateAsync(body);
      setSaleOpen(false);
      setForm(emptySaleForm);
    } catch (err) {
      setCreateError(getErrorMessage(err, 'Erro ao criar venda.'));
    }
  }

  async function handleHerdLotCreated(lot: { id: number; name: string }) {
    await refetchLots();
    setForm((f) => ({
      ...f,
      source: 'lot',
      source_lot_id: String(lot.id),
      title: f.title || lot.name,
    }));
    setSaleOpen(true);
  }

  function saleSubjectLabel(sale: (typeof sales)[number]): string {
    if (sale.source_lot) {
      return `Lote ${sale.source_lot.name} (${sale.source_lot.animals_count} animais)`;
    }
    if (sale.animal) {
      return `${sale.animal.name || `Animal #${sale.animal_id}`}${sale.animal.breed ? ` · ${sale.animal.breed}` : ''}`;
    }
    return sale.animal_id ? `Animal #${sale.animal_id}` : '—';
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vendas"
        subtitle="Animal vivo, cotas de carcaça ou cortes — sem marketplace."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setHerdLotOpen(true)}
              disabled={!propertyId}
            >
              Novo lote de venda
            </Button>
            <Button type="button" onClick={() => setSaleOpen(true)} disabled={!propertyId}>
              Nova venda
            </Button>
          </div>
        }
      />

      <SalesSubnav />

      {!propertyId && (
        <p className="text-sm text-amber-700">Selecione uma propriedade para criar vendas.</p>
      )}

      {isLoading ? (
        <p className="text-sm text-manejai-bark/60">Carregando…</p>
      ) : sales.length === 0 ? (
        <p className="text-sm text-manejai-bark/60">Nenhuma venda ainda.</p>
      ) : (
        <div className="grid gap-3">
          {sales.map((sale) => (
            <div
              key={sale.id}
              className="rounded-2xl border border-manejai-sand bg-white p-4 shadow-sm"
            >
              <Link to={`/vendas/${sale.id}`} className="block">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-manejai-forest">{sale.title}</h2>
                      <Badge tone={statusTone[sale.status]}>{statusLabel[sale.status]}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-manejai-bark/70">
                      {sale.sale_type_label} · {saleSubjectLabel(sale)}
                    </p>
                  </div>
                  <div className="text-right text-sm text-manejai-bark/70">
                    <div>
                      {sale.shares_sold}/{sale.shares_count} itens
                    </div>
                    <div className="font-medium text-manejai-forest">{sale.progress_percent}%</div>
                  </div>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-manejai-sand">
                  <div
                    className="h-full rounded-full bg-manejai-forest"
                    style={{ width: `${Math.min(100, sale.progress_percent)}%` }}
                  />
                </div>
              </Link>
              {sale.status === 'draft' && (
                <div className="mt-3">
                  <Button
                    size="sm"
                    type="button"
                    loading={lotAction.isPending}
                    onClick={() => lotAction.mutate({ id: sale.id, action: 'publish' })}
                  >
                    Abrir para vendas
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        open={saleOpen}
        onClose={() => setSaleOpen(false)}
        title="Nova venda"
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setSaleOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="create-sale-form-modal" loading={createSale.isPending}>
              Criar venda
            </Button>
          </>
        }
      >
        <form id="create-sale-form-modal" className="space-y-3" onSubmit={handleCreateSale}>
          {createError && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {createError}
            </p>
          )}
          <fieldset className="space-y-2 rounded-xl border border-manejai-sand p-3">
            <legend className="px-1 text-sm font-medium text-manejai-forest">Tipo de venda</legend>
            {(
              [
                ['live_animal', 'Animal vivo (live animal)'],
                ['carcass_share', 'Cota de carcaça (carcass share)'],
                ['retail_cuts', 'Cortes/pacotes (retail cuts)'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="sale_type"
                  checked={form.sale_type === value}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      sale_type: value,
                      pricing_unit: defaultPricingUnit(value),
                      slaughterhouse_type:
                        value === 'retail_cuts' ? 'inspected' : f.slaughterhouse_type,
                    }))
                  }
                />
                {label}
              </label>
            ))}
          </fieldset>

          <fieldset className="space-y-2 rounded-xl border border-manejai-sand p-3">
            <legend className="px-1 text-sm font-medium text-manejai-forest">Origem</legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="source"
                checked={form.source === 'animal'}
                onChange={() =>
                  setForm((f) => ({ ...f, source: 'animal', source_lot_id: '' }))
                }
              />
              Um único animal
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="source"
                checked={form.source === 'lot'}
                onChange={() => setForm((f) => ({ ...f, source: 'lot', animal_id: '' }))}
              />
              Lote de animais do sistema
            </label>
          </fieldset>

          {form.source === 'animal' ? (
            <Select
              label="Animal"
              value={form.animal_id}
              placeholder="Selecione…"
              options={animals.map((a) => ({
                value: a.id,
                label: `${a.animal_name || a.internal_number} (${a.internal_number})`,
              }))}
              onChange={(e) => {
                const id = e.target.value;
                const animal = animals.find((a) => String(a.id) === id);
                setForm((f) => ({
                  ...f,
                  animal_id: id,
                  title: f.title || (animal?.animal_name ? String(animal.animal_name) : f.title),
                  total_weight_kg:
                    f.total_weight_kg || String(animal?.current_weight_kg ?? ''),
                }));
              }}
              required
            />
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-0 flex-1">
                  <Select
                    label="Lote de animais"
                    value={form.source_lot_id}
                    placeholder="Selecione um lote…"
                    options={herdLots.map((l) => ({
                      value: l.id,
                      label: `${l.name}${l.lot_type === 'sale' ? ' · Venda/embarque' : ''} (${l.active_animals_count ?? 0} animais)`,
                    }))}
                    onChange={(e) => {
                      const id = e.target.value;
                      const lot = herdLots.find((l) => String(l.id) === id);
                      setForm((f) => ({
                        ...f,
                        source_lot_id: id,
                        title: f.title || lot?.name || f.title,
                      }));
                    }}
                    required
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mb-0.5"
                  onClick={() => setHerdLotOpen(true)}
                >
                  Criar novo lote
                </Button>
              </div>
              <p className="text-xs text-manejai-bark/55">
                Usa os lotes de manejo já cadastrados (inclui tipo “Venda / embarque”).
              </p>
            </div>
          )}

          {(selectedAnimal || selectedHerdLot) && (
            <div className="rounded-xl bg-manejai-cream/60 p-3 text-sm text-manejai-bark">
              {selectedAnimal && (
                <>
                  Preview: {selectedAnimal.animal_name || selectedAnimal.internal_number} ·{' '}
                  {selectedAnimal.breed_name || selectedAnimal.breed_abbreviation || 'sem raça'} ·{' '}
                  {selectedAnimal.current_weight_kg ?? '—'} kg
                </>
              )}
              {selectedHerdLot && (
                <>
                  Preview: lote {selectedHerdLot.name} · tipo {selectedHerdLot.lot_type} ·{' '}
                  {selectedHerdLot.active_animals_count ?? 0} animais
                </>
              )}
            </div>
          )}

          <Input
            label="Título"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
          />
          <Select
            label="Unidade de preço"
            value={form.pricing_unit}
            options={pricingOptions}
            onChange={(e) =>
              setForm((f) => ({ ...f, pricing_unit: e.target.value as SalesPricingUnit }))
            }
          />
          <Input
            label="Preço de referência / unidade"
            type="number"
            step="0.01"
            value={form.price_per_unit}
            onChange={(e) => setForm((f) => ({ ...f, price_per_unit: e.target.value }))}
          />
          <Input
            label="Peso total estimado (kg)"
            type="number"
            step="0.01"
            value={form.total_weight_kg}
            onChange={(e) => setForm((f) => ({ ...f, total_weight_kg: e.target.value }))}
          />
          {form.sale_type !== 'live_animal' && (
            <Input
              label="Data de abate"
              type="date"
              value={form.slaughter_date}
              onChange={(e) => setForm((f) => ({ ...f, slaughter_date: e.target.value }))}
            />
          )}
          <Input
            label="Local de retirada"
            value={form.pickup_location}
            onChange={(e) => setForm((f) => ({ ...f, pickup_location: e.target.value }))}
          />
          {form.sale_type !== 'live_animal' && (
            <Select
              label="Tipo de abatedouro (opcional)"
              value={form.slaughterhouse_type}
              placeholder="Opcional…"
              options={[
                { value: 'custom_exempt', label: 'Custom exempt' },
                { value: 'inspected', label: 'Inspecionado (USDA/DSHS)' },
              ]}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  slaughterhouse_type: e.target.value as 'custom_exempt' | 'inspected' | '',
                }))
              }
            />
          )}
          {form.sale_type === 'retail_cuts' && (
            <Input
              label="Nº do estabelecimento (opcional)"
              value={form.establishment_number}
              onChange={(e) => setForm((f) => ({ ...f, establishment_number: e.target.value }))}
            />
          )}
          <Input
            label="Notas"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </form>
      </Modal>

      <CreateSaleHerdLotModal
        open={herdLotOpen}
        onClose={() => setHerdLotOpen(false)}
        propertyId={propertyId}
        onCreated={handleHerdLotCreated}
      />
    </div>
  );
}
